<template>
    <b-alert v-if="!canViewNetworks" show>
        {{ $t('values.network.cantViewNetworks') }}
    </b-alert>
    <div class="d-flex flex-column flex-xl-row" v-else>
        <div :key="type" :class="`w-100 ${getSideMargins(type)}`" v-for="(networks, type) in networks">
            <div class="network-type-card">
                <div class="d-flex justify-content-between mb-2 pt-1">
                    <h5 class="text-primary">
                        {{ $t(`values.table.${type}`) }}
                    </h5>
                    <b-button
                        id="btn-values-add-in-the-network"
                        v-if="allowedNetworksToEdit[type]"
                        size="sm"
                        variant="outline-primary"
                        @click="onClickAddNetwork(type)"
                    >
                        <div class="d-flex flex-row align-items-center">
                            <i class="fas fa-user-plus mr-1" />
                            <span>{{ $t('form.button.add') }}</span>
                        </div>
                    </b-button>
                </div>
                <div class="scroll">
                    <div class="p-2" :key="k" v-for="(valuer, k) in networks">
                        <div class="d-flex flex-row">
                            <div class="d-flex mr-auto">
                                <avatar class="mr-2" :size="35" :src="valuer.picture" :username="valuer.name" />
                                <div>
                                    <p class="font-weight-bold text-primary">
                                        {{ valuer.name }}
                                    </p>
                                    <span class="text-muted" style="font-size: 11px">{{ valuer.occupation }}</span>
                                </div>
                            </div>

                            <b-button
                                id="btn-values-delete-from-network"
                                v-if="allowedNetworksToEdit[type]"
                                variant="link"
                                class="float-right"
                                @click="onClickDeleteNetwork(valuer.network)"
                            >
                                <img src="@/assets/icons/garbage.svg" width="15" height="15" />
                            </b-button>
                        </div>
                        <hr />
                    </div>
                </div>
                <div style="font-size: 12px" class="text-primary font-weight-bold mt-2">
                    {{ $t('values.amount', { amount: networks.length }) }}
                </div>
            </div>
        </div>
        <new-network-modal
            :user="user"
            :network-type="networkType"
            :open="showNewNetworkModal"
            @close="showNewNetworkModal = false"
            @cancel="showNewNetworkModal = false"
            @networks-created="onCreateNewNetworks"
        />
    </div>
</template>
<script>
import i18n from '@/i18n'
import Avatar from 'vue-avatar'
import { userPermissionsMixin } from '@/mixins'
import { intersectionBy, intersection } from 'lodash'
import { proceedWithConfirmation } from '@/lib/sweet-alert'
import { getUserNetworks, deleteNetwork } from '@/views/values/api'
import NewNetworkModal from './components/NewNetwork'

const networkOperation = {
    team: 'allow_committee_to_edit_team_network',
    leader: 'allow_committee_to_edit_leader_network',
    peer: 'allow_committee_to_edit_peers_and_internal_clients_networks',
}

export default {
    mixins: [userPermissionsMixin],
    props: {
        user: {
            type: Object,
            required: true,
        },
    },
    components: {
        Avatar,
        NewNetworkModal,
    },
    data() {
        return {
            networks: {},
            networkType: '',
            showNewNetworkModal: false,
        }
    },
    mounted() {
        this.getNetworks()
        this.$emit('allowed-networks-to-edit', this.allowedNetworksToEdit)
    },
    methods: {
        onCreateNewNetworks(createdNetworks) {
            this.$emit('networks-created', createdNetworks)
            this.getNetworks()
            this.showNewNetworkModal = false
        },
        getSideMargins(type) {
            if ([0, 1].includes(Object.keys(this.networks).indexOf(type))) {
                return 'mr-0 mr-xl-3'
            }
            return ''
        },
        getNetworks() {
            getUserNetworks(this.user.id).then((user) => {
                if (user.valuers) {
                    this.networks = {
                        leader: user.valuers.filter((valuer) => valuer.network.type == 'leader'),
                        team: user.valuers.filter((valuer) => valuer.network.type == 'team'),
                        peer: user.valuers.filter((valuer) => valuer.network.type == 'peer'),
                    }
                }
                this.$emit('loaded')
            })
        },
        onClickAddNetwork(type) {
            this.networkType = type
            this.showNewNetworkModal = true
        },
        onClickDeleteNetwork(network) {
            proceedWithConfirmation({
                title: i18n.t('notification.areYouSureRemove'),
                onConfirm: () => deleteNetwork(network.id),
                callback: () => {
                    const type = network.type
                    this.networks[type] = this.networks[type].filter((item) => {
                        return item.id !== network.id
                    })
                    this.$emit('network-deleted', network)
                    this.getNetworks()
                },
            })
        },
        canEditNetwork(type) {
            if (this.currentUserHasRolePermission) {
                return true
            }

            const operation = networkOperation[type]

            const userValuesPermissions = this.user.permissions.values.consolidated
            const currentUserValuesPermissions = this.currentUser.permissions.values.consolidated

            const userHasEditNetworkPermission = this.verifyIfEditNetworksIncludesType(this.user.edit_networks, type)
            const currentUserHasEditNetworkPermission = this.verifyIfEditNetworksIncludesType(
                this.currentUser.edit_networks,
                type
            )

            const userHasEventPermission = this.userHasEvent ? userValuesPermissions[operation] : true

            const currentUserHasEventPermission = this.currentUserHasEvent
                ? currentUserValuesPermissions[operation]
                : true

            const committees = this.committeesWhereTheCurrentUserIsEvaluator
            const hasCommitteePermission = this.userHasEvent
                ? this.verifyIfSomeCommitteeHasEventPermission(committees, operation, this.user.event_user.event_id)
                : false

            return (
                (this.isTheSameUser && userHasEventPermission && userHasEditNetworkPermission) ||
                (this.isManager && currentUserHasEventPermission && currentUserHasEditNetworkPermission) ||
                hasCommitteePermission
            )
        },
        verifyIfEditNetworksIncludesType(editNetworks, type) {
            return editNetworks.map((i) => i.type).includes(type)
        },
        verifyIfSomeCommitteeHasEventPermission(committees, operation, eventId) {
            const committeePermissions = committees.reduce((permissions, committee) => {
                const committeeEventPermissions = committee.event_permissions.filter((permission) => {
                    return permission.event_id == eventId && permission.operation.title == operation
                })

                return permissions.concat(committeeEventPermissions)
            }, [])

            return committeePermissions.length > 0
        },
    },
    computed: {
        allowedNetworksToEdit() {
            return {
                peer: this.canEditNetwork('peer'),
                team: this.canEditNetwork('team'),
                leader: this.canEditNetwork('leader'),
            }
        },
        currentUser() {
            return this.$store.state.currentUser.user
        },
        isTheSameUser() {
            return this.user.id == this.currentUser.id
        },
        isManager() {
            return this.user.manager_id == this.currentUser.id
        },
        userHasEvent() {
            return this.user.event_user !== null
        },
        currentUserHasEvent() {
            return this.currentUser.event_user !== null
        },
        currentUserHasRolePermission() {
            const allowedRoles = ['administrator', 'local_administrator', 'god', 'is_values_master']

            const rolesIntersection = intersection(allowedRoles, this.currentUser.has_roles)

            return rolesIntersection.length > 0
        },
        committeesWhereTheCurrentUserIsEvaluator() {
            return intersectionBy(this.currentUser.evaluator_committees, this.user.rated_committees, 'id')
        },
        canViewNetworks() {
            return this.$store.getters['getValuesPermissions'].allow_committee_to_view_evaluated_users_networks
        },
    },
}
</script>

<style lang="sass" scoped>
$border-style: solid
$border-width: 4px
$primary-color: rgb(58, 178, 227)
$secondary-color: rgb(200, 206, 211)

.network-type-card
    padding: 10px 20px 10px 20px
    border-style: rounded
    border-left: solid $primary-color
    border-left-width: $border-width
    border-bottom-left-radius: $border-width
    border-bottom-right-radius: $border-width
    border-right: 1px solid $secondary-color
    border-bottom: 1px solid $secondary-color
    box-shadow: 1px 2px rgb(235,235,235)

.scroll
    height: 200px
    overflow-y: auto

.scroll::-webkit-scrollbar
    width: 6px
    background-color: $secondary-color

.scroll::-webkit-scrollbar-thumb
    background-color: rgba(0,0,0,.2)

.scroll::-webkit-scrollbar-track
    background: hsla(0,0%,100%,.1)

.text-primary
    color: $primary-color

.btn-sm
    border-radius: 0.4rem
    padding: 0px 10px 0px 10px

p
    margin-bottom: 0px

hr
    margin-top: 7px
    margin-bottom: 0px
</style>
